import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Select, 
  InputNumber,
  DatePicker,
  message, 
  Popconfirm,
  Card,
  Typography,
  Tag,
  Tooltip,
  Row,
  Col,
  Switch,
  Divider
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  ReloadOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  UserOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { giftService, Gift, Event, GiftGiver } from '../../../services/giftService';
import dayjs from 'dayjs';
import styles from './GiftsPage.module.css';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const GiftsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [givers, setGivers] = useState<GiftGiver[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGift, setEditingGift] = useState<Gift | null>(null);
  const [form] = Form.useForm();
  const [filterGiver, setFilterGiver] = useState<number | undefined>();
  const [filterEvent, setFilterEvent] = useState<number | undefined>();
  const [filterType, setFilterType] = useState<string | undefined>();
  const [filterReturned, setFilterReturned] = useState<boolean | undefined>();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total: number, range: [number, number]) => 
      `${range[0]}-${range[1]} của ${total} tiền mừng`
  });
  // Filtering is now handled by the backend API

  const eventId = searchParams.get('event_id');
  const giverId = searchParams.get('giver_id');

  const fetchGifts = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const params: any = {
        page,
        limit: pageSize
      };
      if (eventId) params.event_id = parseInt(eventId);
      if (giverId) params.giver_id = parseInt(giverId);
      if (filterGiver) params.giver_id = filterGiver;
      if (filterEvent) params.event_id = filterEvent;
      if (filterType) params.gift_type = filterType;
      if (filterReturned !== undefined) params.is_returned = filterReturned;
      
      const response = await giftService.getGifts(params);
      setGifts(response.data);
      
      // Cập nhật pagination info
      const paginationData = response.pagination;
      if (paginationData) {
        setPagination(prev => ({
          ...prev,
          current: paginationData.page,
          total: paginationData.total,
          pageSize: paginationData.limit
        }));
      }
    } catch (error) {
      message.error('Không thể tải danh sách tiền mừng');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await giftService.getEvents();
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchGivers = async () => {
    try {
      const response = await giftService.getGiftGivers({ limit: 1000 }); // Lấy tất cả givers
      setGivers(response.data);
    } catch (error) {
      console.error('Error fetching givers:', error);
    }
  };

  useEffect(() => {
    // Load events và givers trước, sau đó mới load gifts
    const loadData = async () => {
      setLoadingData(true);
      try {
        await Promise.all([fetchEvents(), fetchGivers()]);
        fetchGifts();
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, [eventId, giverId]);

  // Effect để reload khi filter thay đổi
  useEffect(() => {
    fetchGifts(1, pagination.pageSize);
  }, [filterGiver, filterEvent, filterType, filterReturned]);

  const handleCreate = () => {
    setEditingGift(null);
    form.resetFields();
    if (eventId) {
      form.setFieldsValue({ event_id: parseInt(eventId) });
    }
    if (giverId) {
      form.setFieldsValue({ giver_id: parseInt(giverId) });
    }
    setModalVisible(true);
  };

  const handleEdit = (record: Gift) => {
    setEditingGift(record);
    form.setFieldsValue({
      event_id: record.event_id,
      giver_id: record.giver_id,
      amount: record.amount,
      gift_type: record.gift_type,
      gift_name: record.gift_name,
      gift_value: record.gift_value,
      notes: record.notes,
      is_returned: record.is_returned,
      return_date: record.return_date ? dayjs(record.return_date) : null,
      return_event_id: record.return_event_id,
      return_amount: record.return_amount,
      return_notes: record.return_notes
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await giftService.deleteGift(id);
      message.success('Xóa tiền mừng thành công');
      fetchGifts();
    } catch (error) {
      message.error('Không thể xóa tiền mừng');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        ...values,
        return_date: values.return_date ? values.return_date.format('YYYY-MM-DD') : null
      };

      if (editingGift) {
        await giftService.updateGift(editingGift.id, data);
        message.success('Cập nhật tiền mừng thành công');
      } else {
        await giftService.createGift(data);
        message.success('Tạo tiền mừng thành công');
      }

      setModalVisible(false);
      fetchGifts();
    } catch (error) {
      message.error('Có lỗi xảy ra');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const getGiftTypeLabel = (giftType: string) => {
    const labels: Record<string, string> = {
      cash: 'Tiền mặt',
      gift: 'Quà tặng',
      other: 'Khác'
    };
    return labels[giftType] || giftType;
  };

  const getGiftTypeColor = (giftType: string) => {
    const colors: Record<string, string> = {
      cash: 'green',
      gift: 'blue',
      other: 'default'
    };
    return colors[giftType] || 'default';
  };

  const getEventName = (eventId: number, eventName?: string) => {
    // Ưu tiên sử dụng event_name từ API response
    if (eventName) return eventName;
    
    const event = events.find(e => e.id === eventId);
    if (!event) {
      // Nếu events chưa load xong, hiển thị loading
      if (events.length === 0) return 'Đang tải...';
      return 'Sự kiện không tồn tại';
    }
    return event.name;
  };

  const getGiverName = (giverId: number, giverName?: string) => {
    // Ưu tiên sử dụng giver_name từ API response
    if (giverName) return giverName;
    
    const giver = givers.find(g => g.id === giverId);
    if (!giver) {
      // Nếu givers chưa load xong, hiển thị loading
      if (givers.length === 0) return 'Đang tải...';
      return 'Người mừng không tồn tại';
    }
    return giver.full_name;
  };

  const getRelationshipLabel = (relationship: string) => {
    const labels: Record<string, string> = {
      family_internal: 'Gia đình nội',
      family_external: 'Gia đình ngoại',
      friend_bride: 'Bạn cô dâu',
      friend_groom: 'Bạn chú rể',
      colleague: 'Đồng nghiệp',
      business_partner: 'Đối tác kinh doanh',
      other: 'Khác'
    };
    return labels[relationship] || relationship;
  };

  const columns = [
    {
      title: 'Người mừng',
      dataIndex: 'giver_id',
      key: 'giver_id',
      render: (giverId: number, record: Gift) => {
        const giver = givers.find(g => g.id === giverId);
        return (
          <Space direction="vertical" size="small">
            <Space>
              <UserOutlined />
              <strong>{getGiverName(giverId, record.giver_name)}</strong>
            </Space>
            {(giver || record.relationship) && (
              <Tag color="blue">
                {getRelationshipLabel(record.relationship || giver?.relationship || '')}
              </Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Sự kiện',
      dataIndex: 'event_id',
      key: 'event_id',
      render: (eventId: number, record: Gift) => (
        <Space>
          <CalendarOutlined />
          {getEventName(eventId, record.event_name)}
        </Space>
      )
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => (
        <Space>
          <DollarOutlined />
          <strong style={{ color: '#3f8600' }}>
            {formatCurrency(amount)}
          </strong>
        </Space>
      ),
      sorter: (a: Gift, b: Gift) => a.amount - b.amount
    },
    {
      title: 'Loại',
      dataIndex: 'gift_type',
      key: 'gift_type',
      render: (giftType: string) => (
        <Tag color={getGiftTypeColor(giftType)}>
          {getGiftTypeLabel(giftType)}
        </Tag>
      )
    },
    {
      title: 'Tên quà',
      dataIndex: 'gift_name',
      key: 'gift_name',
      render: (text: string) => text || '-'
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      render: (text: string) => text || '-',
      ellipsis: true
    },
    {
      title: 'Đã đáp lễ',
      dataIndex: 'is_returned',
      key: 'is_returned',
      render: (isReturned: boolean) => (
        <Tag color={isReturned ? 'green' : 'orange'} icon={isReturned ? <CheckCircleOutlined /> : null}>
          {isReturned ? 'Đã đáp lễ' : 'Chưa đáp lễ'}
        </Tag>
      )
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_: any, record: Gift) => (
        <Space>
          <Tooltip title="Chỉnh sửa">
            <Button 
              type="link" 
              icon={<EditOutlined />} 
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Bạn có chắc muốn xóa tiền mừng này?"
            onConfirm={() => handleDelete(record.id)}
            okText="Có"
            cancelText="Không"
          >
            <Tooltip title="Xóa">
              <Button 
                type="link" 
                danger 
                icon={<DeleteOutlined />} 
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className={styles.container}>
      <Card className={styles.header}>
        <div className={styles.headerContent}>
          <Title level={2} className={styles.title}>
            Quản lý tiền mừng
          </Title>
          <Space>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={() => fetchGifts()}
              loading={loading}
            >
              Làm mới
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Thêm tiền mừng
            </Button>
          </Space>
        </div>
      </Card>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={5}>
            <Select
              allowClear
              showSearch
              placeholder="Lọc theo người mừng"
              style={{ width: '100%' }}
              value={filterGiver}
              onChange={setFilterGiver}
              optionFilterProp="children"
              filterOption={(input, option) => {
                const text = option?.children?.toString() || '';
                return text.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {givers.map(giver => (
                <Option key={giver.id} value={giver.id}>
                  {giver.full_name} - {getRelationshipLabel(giver.relationship)}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={5}>
            <Select
              allowClear
              showSearch
              placeholder="Lọc theo sự kiện"
              style={{ width: '100%' }}
              value={filterEvent}
              onChange={setFilterEvent}
              optionFilterProp="children"
              filterOption={(input, option) => {
                const text = option?.children?.toString() || '';
                return text.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {events.map(event => (
                <Option key={event.id} value={event.id}>
                  {event.name} - {dayjs(event.event_date).format('DD/MM/YYYY')}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              allowClear
              placeholder="Lọc theo loại"
              style={{ width: '100%' }}
              value={filterType}
              onChange={setFilterType}
            >
              <Option value="cash">Tiền mặt</Option>
              <Option value="gift">Quà tặng</Option>
              <Option value="other">Khác</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              allowClear
              placeholder="Lọc theo trạng thái"
              style={{ width: '100%' }}
              value={filterReturned}
              onChange={setFilterReturned}
            >
              <Option value={true}>Đã đáp lễ</Option>
              <Option value={false}>Chưa đáp lễ</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Button 
              onClick={() => { 
                setFilterGiver(undefined); 
                setFilterEvent(undefined); 
                setFilterType(undefined); 
                setFilterReturned(undefined); 
                fetchGifts(1, pagination.pageSize);
              }}
              style={{ width: '100%' }}
            >
              Xóa bộ lọc
            </Button>
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={gifts}
          rowKey="id"
          loading={loading || loadingData}
          pagination={pagination}
          onChange={(paginationInfo) => {
            if (paginationInfo.current) {
              fetchGifts(paginationInfo.current, paginationInfo.pageSize || 10);
            }
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title={editingGift ? 'Chỉnh sửa tiền mừng' : 'Thêm tiền mừng mới'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            gift_type: 'cash'
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="event_id"
                label="Sự kiện"
                rules={[{ required: true, message: 'Vui lòng chọn sự kiện' }]}
              >
                <Select placeholder="Chọn sự kiện" disabled={!!eventId}>
                  {events.map(event => (
                    <Option key={event.id} value={event.id}>
                      {event.name} - {dayjs(event.event_date).format('DD/MM/YYYY')}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="giver_id"
                label="Người mừng"
                rules={[{ required: true, message: 'Vui lòng chọn người mừng' }]}
              >
                <Select 
                  placeholder="Chọn người mừng" 
                  disabled={!!giverId}
                  showSearch
                  filterOption={(input, option) => {
                    const optionText = option?.children?.toString() || '';
                    return optionText.toLowerCase().includes(input.toLowerCase());
                  }}
                  optionFilterProp="children"
                >
                  {givers.map(giver => (
                    <Option key={giver.id} value={giver.id}>
                      {giver.full_name} - {getRelationshipLabel(giver.relationship)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="amount"
                label="Số tiền"
                rules={[{ required: true, message: 'Vui lòng nhập số tiền' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Nhập số tiền"
                  formatter={(value: number | string | undefined) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value: string | undefined) => value ? value.replace(/\$\s?|(,*)/g, '') : ''}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="gift_type"
                label="Loại"
                rules={[{ required: true, message: 'Vui lòng chọn loại' }]}
              >
                <Select placeholder="Chọn loại">
                  <Option value="cash">Tiền mặt</Option>
                  <Option value="gift">Quà tặng</Option>
                  <Option value="other">Khác</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="gift_name"
                label="Tên quà tặng"
              >
                <Input placeholder="Tên quà tặng (nếu có)" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="gift_value"
                label="Giá trị quà"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Giá trị quà"
                  formatter={(value: number | string | undefined) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value: string | undefined) => value ? value.replace(/\$\s?|(,*)/g, '') : ''}
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label="Ghi chú"
          >
            <TextArea 
              rows={3} 
              placeholder="Nhập ghi chú hoặc click vào các nút bên dưới"
            />
          </Form.Item>
          
          <Form.Item label="Ghi chú mẫu">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {[
                'Không đi'
              ].map((note, index) => (
                <Button
                  key={index}
                  size="small"
                  type="dashed"
                  onClick={() => {
                    const currentNotes = form.getFieldValue('notes') || '';
                    const newNotes = currentNotes ? `${currentNotes}, ${note}` : note;
                    form.setFieldsValue({ notes: newNotes });
                  }}
                  style={{ marginBottom: '4px' }}
                >
                  {note}
                </Button>
              ))}
            </div>
          </Form.Item>

          {editingGift && (
            <>
              <Divider>Thông tin đáp lễ</Divider>
              
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="is_returned"
                    label="Đã đáp lễ"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="return_date"
                    label="Ngày đáp lễ"
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      format="DD/MM/YYYY"
                      placeholder="Chọn ngày"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="return_event_id"
                    label="Sự kiện đáp lễ"
                  >
                    <Select placeholder="Chọn sự kiện đáp lễ" allowClear>
                      {events.map(event => (
                        <Option key={event.id} value={event.id}>
                          {event.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="return_amount"
                    label="Số tiền đáp lễ"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="Số tiền đáp lễ"
                      formatter={(value: number | string | undefined) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value: string | undefined) => value ? value.replace(/\$\s?|(,*)/g, '') : ''}
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="return_notes"
                label="Ghi chú đáp lễ"
              >
                <TextArea 
                  rows={2} 
                  placeholder="Ghi chú về việc đáp lễ"
                />
              </Form.Item>
            </>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingGift ? 'Cập nhật' : 'Thêm tiền mừng'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Hủy
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GiftsPage; 